import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { auth } from '../firebase'; // Adjust this import path as needed

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProfile();
    checkFollowStatus();
  }, [username]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public-profile/${username}`);
      setProfile(response.data.profile);
      setImages(response.data.images);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatus = async () => {
    if (!auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/follow/${username}/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setIsFollowing(response.data.isFollowing);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!auth.currentUser) {
      alert('Please log in to follow users');
      return;
    }

    try {
      const token = await auth.currentUser.getIdToken();
      if (isFollowing) {
        await axios.delete(`${process.env.REACT_APP_API_URL}/follow/${username}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/follow/${username}`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      // Refresh profile data
      fetchProfile();
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  // ... keep your existing styles ...
  const styles = {
    // ... your existing styles ...
    followButton: {
      padding: '10px 20px',
      backgroundColor: isFollowing ? '#dc3545' : '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      marginTop: '10px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '90%',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative'
    },
    closeButton: {
      position: 'absolute',
      right: '10px',
      top: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#333'
    }
  };

  if (loading) {
    return <div style={styles.loadingText}>Loading profile...</div>;
  }

  if (!profile) {
    return <div style={styles.errorText}>Profile not found</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.profileHeader}>
        <h1 style={styles.username}>{profile.anonymous_username}</h1>
        <p style={styles.bio}>{profile.bio || 'No bio provided'}</p>
        <div style={styles.stats}>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{images.length}</div>
            <div style={styles.statLabel}>Gallery Count</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{profile.follower_count || 0}</div>
            <div style={styles.statLabel}>Followers</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statNumber}>{profile.following_count || 0}</div>
            <div style={styles.statLabel}>Following</div>
          </div>
        </div>
        <p style={styles.memberSince}>
          Member since {new Date(profile.created_at).toLocaleDateString()}
        </p>
        {auth.currentUser && auth.currentUser.displayName !== profile.anonymous_username && (
          <button 
            style={styles.followButton}
            onClick={handleFollowToggle}
          >
            {isFollowing ? 'Unfollow' : 'Follow'}
          </button>
        )}
      </div>

      <h2>Gallery</h2>
      <div style={styles.imageGrid}>
        {images.map((image) => (
          <div 
            key={image.id} 
            style={styles.imageCard}
            onClick={() => setSelectedImage(image)}
          >
            <img
              src={image.image_url}
              alt={image.prompt}
              style={styles.image}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/300?text=Image+Not+Found';
              }}
            />
          </div>
        ))}
      </div>

      {selectedImage && (
        <div style={styles.modal} onClick={() => setSelectedImage(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button
              style={styles.closeButton}
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.prompt}
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
            <div style={{ marginTop: '20px' }}>
              <p><strong>Created:</strong> {new Date(selectedImage.created_at).toLocaleString()}</p>
              <p><strong>Prompt:</strong> {selectedImage.prompt}</p>
            </div>
          </div>
        </div>
      )}

      {images.length === 0 && (
        <p style={styles.errorText}>No public images available</p>
      )}
    </div>
  );
};

export default PublicProfile;
